'use client';

import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import Image from 'next/image';
import { color, spacing } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import SectionWrapper from '@/components/common/SectionWrapper';

const servicesData = [
    {
        id: 'auction-access',
        titleKey: 'auctionAccess',
        statKey: 'unlimited',
        descriptionKey: 'unlimitedDescription',
        image: '/homepage/homepage-service-auction-access.jpg',
        icon: '/homepage/ServicesIcon.svg',
    },
    {
        id: 'domestic-transportation',
        titleKey: 'domesticTransportation',
        statKey: 'domesticCount',
        descriptionKey: 'domesticDescription',
        image: '/homepage/homepage-service-domestic-transportation.jpg',
        icon: '/homepage/ServicesIcon.svg',
    },
    {
        id: 'global-shipping',
        titleKey: 'globalShipping',
        statKey: 'globalCount',
        descriptionKey: 'globalDescription',
        image: '/homepage/homepage-service-global-shipping.jpg',
        icon: '/homepage/ServicesIcon.svg',
    },
];

export default function Services() {
    const t = useTranslations('HomePage.services');

    return (
        <SectionWrapper backgroundColor={color.white[0]} contentAlignItems="center" contentJustifyContent="center">
            <Stack
                spacing={{ xs: spacing[48], sm: spacing[64], md: spacing[80] }}
                alignItems="center"
                width="100%"
                sx={{
                    marginBottom: spacing[16],
                }}
            >
                <Typography
                    variant="heading5bold"
                    color={color.gray[80]}
                    textAlign="center"
                    component="h2"
                    sx={{
                        maxWidth: { xs: '400px', sm: '460px', md: '500px' },
                    }}
                >
                    {t('title')}
                </Typography>

                <Stack
                    width="100%"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(3, minmax(0, 1fr))',
                        },
                        rowGap: { xs: spacing[48], sm: spacing[64], md: spacing[32] },
                        columnGap: { xs: spacing[48], sm: spacing[24], md: spacing[32] },
                    }}
                >
                    {servicesData.map((service, index) => {
                        const isLastAndOdd = index === servicesData.length - 1 && servicesData.length % 2 === 1;
                        const serviceRoute = service.id === 'general-info' ? '/services' : `/services/${service.id}`;

                        return (
                            <Box
                                key={service.id}
                                component={Link}
                                href={serviceRoute}
                                sx={{
                                    textDecoration: 'none',
                                    display: 'block',
                                    width: '100%',
                                    '&:focus': {
                                        outline: 'none',
                                    },
                                    '&:focus-visible': {
                                        outline: `2px solid ${color.gray[80]}`,
                                        outlineOffset: '5px',
                                    },
                                    ...(isLastAndOdd
                                        ? {
                                              gridColumn: { sm: '1 / -1', md: 'auto' },
                                              justifySelf: { sm: 'center', md: 'stretch' },
                                              maxWidth: { sm: `calc((100% - ${spacing[24]}) / 2)`, md: 'none' },
                                          }
                                        : {}),
                                }}
                            >
                                <Stack
                                    spacing={spacing[16]}
                                    alignItems="center"
                                    sx={{
                                        width: '100%',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                        },
                                    }}
                                >
                                    <Stack direction="column" spacing={0} width="100%">
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={spacing[16]}
                                            sx={{
                                                backgroundColor: color.brand[50],
                                                padding: spacing[24],
                                            }}
                                        >
                                            <Typography variant="heading2semibold" color={color.white[0]} flex={1}>
                                                {t(`cards.${service.titleKey}`)}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    width: '24px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {/* Small static icon, not a content image — next/image would add overhead here. */}
                                                {/* react-doctor-disable-next-line nextjs-no-img-element */}
                                                <img
                                                    src={service.icon}
                                                    alt=""
                                                    style={{ width: '100%', height: '100%' }}
                                                />
                                            </Box>
                                        </Stack>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                aspectRatio: '1200/900',
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Image
                                                src={service.image}
                                                alt={t(`cards.${service.titleKey}`)}
                                                fill
                                                priority
                                                sizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 33vw"
                                                style={{
                                                    objectFit: 'cover',
                                                    objectPosition: '50% 50%',
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                    <Stack spacing={spacing[8]} alignItems="center" textAlign="center">
                                        <Typography
                                            variant="heading5bold"
                                            color={color.gray[80]}
                                            maxWidth={{ xs: '230px', sm: '260px', md: '290px' }}
                                        >
                                            {t(`cards.${service.statKey}`)}
                                        </Typography>
                                        <Typography variant="heading1regular" color={color.gray[60]} maxWidth="260px">
                                            {t(`cards.${service.descriptionKey}`)}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            </Stack>
        </SectionWrapper>
    );
}
